import React, { useEffect, useState } from 'react';

import type { DataExportTemplate, DataExportTemplateColumnOptions, MailersTemplate } from 'bundles/connect/types';
import { Button, Card, Form, InputGroup, Nav } from 'react-bootstrap-5';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faFileSpreadsheet, faHouseBuilding, faMagnifyingGlass } from '@fortawesome/pro-solid-svg-icons';
import NewTemplateModal from '../data_exports/new_template_modal';
import EditTemplateModal from '../data_exports/edit_template_modal';
import TemplateList from '../data_exports/template_list';
import { deleteDataExportTemplate, deleteMailersTemplates } from 'bundles/connect/helpers/prospect_api';
import MailersTemplatesSettingsList from '../mailers/templates/mailers_templates_settings_list';

type Props = {
  callListId: number | null;
  columnOptions: DataExportTemplateColumnOptions;
  dataExportTemplates: DataExportTemplate[];
  mailerExportTemplates: MailersTemplate[];
}

const DATA_EXPORT_TAB = 'data_exports';
const MAILER_EXPORT_TAB = 'mailer_exports';

export default function ExportTemplateSettings({
  callListId, dataExportTemplates, columnOptions, mailerExportTemplates
}: Props) {
  const [tab, setTab] = useState(DATA_EXPORT_TAB);
  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<DataExportTemplate>();
  const [searchText, setSearchText] = useState('');
  const [searchDataTemplateResults, setSearchDataTemplateResults] = useState(dataExportTemplates);
  const [searchMailerTemplateResults, setSearchMailerTemplateResults] = useState(mailerExportTemplates);

  useEffect(() => {
    if (!searchText) {
      setSearchDataTemplateResults(dataExportTemplates);
      setSearchMailerTemplateResults(mailerExportTemplates);
    } else {
      setSearchDataTemplateResults(prev => prev.filter(e => e.name.toLowerCase().includes(searchText)));
      setSearchMailerTemplateResults(prev => prev.filter(e => e.title.toLowerCase().includes(searchText)));
    }
  }, [searchText]);

  const handleTemplateAdded = (template: DataExportTemplate) => {
    setSearchDataTemplateResults([...searchDataTemplateResults, template]);
  };

  const handleDeleteTemplate = (template: DataExportTemplate) => {
    if (confirm(`Are you sure you want to delete template "${template.name}"?`)) {
      deleteDataExportTemplate(template.id).then(() => {
        setSearchDataTemplateResults(prev => prev.filter(t => t.id !== template.id));
      });
    }
  };

  const handleEditTemplate = (template: DataExportTemplate) => {
    setEditingTemplate(template);
  };

  const handleDeleteMailerTemplate = (template: MailersTemplate) => {
    if (confirm(`Are you sure you want to delete template "${template.title}"?`)) {
      deleteMailersTemplates([template.id]).then(() => {
        setSearchMailerTemplateResults(prev => prev.filter(t => t.id !== template.id));
      });
    }
  };

  const handleTemplateUpdated = (template: DataExportTemplate) => {
    setSearchDataTemplateResults(prev => prev.map((t) => {
      if (t.id == template.id) return template;

      return t;
    }));
  };

  return (
    <Card>
      <Card.Header className="p-6">
        <div className="d-flex align-items-center justify-content-between">
          <h5 className="mb-0">Export Templates</h5>

          <Button variant="outline-primary" onClick={() => setShowModal(true)}>
            <FontAwesomeIcon icon={faFileSpreadsheet}/>
            New Export Template
          </Button>

          <NewTemplateModal
            columnOptions={columnOptions}
            show={showModal}
            onHide={() => setShowModal(false)}
            onTemplateAdded={handleTemplateAdded}
          />
          <EditTemplateModal
            columnOptions={columnOptions}
            template={editingTemplate}
            onHide={() => setEditingTemplate(undefined)}
            onTemplateUpdated={handleTemplateUpdated}
          />
        </div>
      </Card.Header>
      <Card.Body className="px-6">
        <Form.Label className="mb-0">Search Templates</Form.Label>
        <InputGroup className="my-2">
          <InputGroup.Text>
            <FontAwesomeIcon icon={faMagnifyingGlass}/>
          </InputGroup.Text>
          <Form.Control
            placeholder="Search by template name"
            type="text"
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
          />
        </InputGroup>
      </Card.Body>
      <Nav activeKey={tab} variant="tabs" onSelect={key => key && setTab(key)}>
        <Nav.Item>
          <Nav.Link className="border-start-0 py-2" eventKey={DATA_EXPORT_TAB}>
            <FontAwesomeIcon icon={faHouseBuilding}/>
            Properties & Activities ({searchDataTemplateResults.length})
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link className="py-2" eventKey={MAILER_EXPORT_TAB}>
            <FontAwesomeIcon icon={faEnvelope}/>
            Mailers ({searchMailerTemplateResults.length})
          </Nav.Link>
        </Nav.Item>
      </Nav>
      <Card.Body className="px-6">
        {tab === DATA_EXPORT_TAB && (
          <TemplateList
            templates={searchDataTemplateResults}
            onDeleteTemplate={handleDeleteTemplate}
            onEditTemplate={handleEditTemplate}
          />
        )}
        {tab === MAILER_EXPORT_TAB && (
          <MailersTemplatesSettingsList
            callListId={callListId}
            templates={searchMailerTemplateResults}
            onDeleteTemplate={handleDeleteMailerTemplate}
          />
        )}
      </Card.Body>
    </Card>
  );
}
